import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ClipboardList, Search, ChevronLeft, ChevronRight, Loader2, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  auth: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  upload: 'bg-blue-50 text-blue-700 border-blue-200',
  processing: 'bg-violet-50 text-violet-700 border-violet-200',
  admin: 'bg-red-50 text-red-700 border-red-200',
  correction: 'bg-amber-50 text-amber-700 border-amber-200',
  rules: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  enrichment: 'bg-pink-50 text-pink-700 border-pink-200',
};

const ACTION_LABELS = {
  login: 'Logged in',
  register: 'Registered',
  password_reset: 'Password reset',
  data_upload: 'Uploaded data',
  data_cleanse: 'Cleansed data',
  classification_started: 'Started classification',
  bulk_reclassify: 'Bulk reclassified',
  reclassify_incomplete: 'Reclassified incomplete',
  data_export: 'Exported data',
  supplier_merge: 'Merged suppliers',
  role_change: 'Changed user role',
  user_invited: 'Invited user',
  user_deleted: 'Deleted user',
  org_name_change: 'Updated organization',
};

export const AuditLog = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const perPage = 30;

  const fetchLogs = useCallback(async (pg, category, email) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: perPage, skip: (pg - 1) * perPage });
      if (category) params.set('category', category);
      if (email) params.set('user_email', email);
      const res = await axios.get(`${API}/admin/audit-log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLogs(1, '', ''); }, [fetchLogs]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchLogs(newPage, filterCategory, searchEmail);
  };

  const handleFilterChange = (category) => {
    setFilterCategory(category);
    setPage(1);
    fetchLogs(1, category, searchEmail);
  };

  const handleSearchChange = (email) => {
    setSearchEmail(email);
    setPage(1);
    fetchLogs(1, filterCategory, email);
  };

  const clearFilters = () => {
    setFilterCategory('');
    setSearchEmail('');
    setPage(1);
    fetchLogs(1, '', '');
  };

  const totalPages = Math.ceil(total / perPage);

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const renderDetails = (log) => {
    const d = log.details || {};
    const parts = [];
    if (d.filename) parts.push(d.filename);
    if (d.records) parts.push(`${d.records} records`);
    if (d.taxonomy) parts.push(`taxonomy: ${d.taxonomy}`);
    if (d.format) parts.push(`format: ${d.format}`);
    if (d.original && d.normalized) parts.push(`${d.original} → ${d.normalized}`);
    if (d.role) parts.push(`role: ${d.role}`);
    if (d.email) parts.push(d.email);
    if (d.organization_name) parts.push(`org: ${d.organization_name}`);
    if (d.quality_score) parts.push(`quality: ${d.quality_score}%`);
    return parts.join(' · ') || '-';
  };

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-1">Activity Audit Log</h1>
        <p className="text-slate-600 text-sm">Monitor user actions across your organization</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="audit-search-email"
            value={searchEmail}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Filter by user email..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-400" />
          <select
            data-testid="audit-category-filter"
            value={filterCategory}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            <option value="auth">Authentication</option>
            <option value="upload">Uploads</option>
            <option value="processing">Processing</option>
            <option value="admin">Admin</option>
            <option value="correction">Corrections</option>
            <option value="rules">Rules</option>
            <option value="enrichment">Enrichment</option>
          </select>
        </div>
        {(filterCategory || searchEmail) && (
          <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="audit-clear-filters">
            <X size={12} /> Clear
          </button>
        )}
        <span className="text-xs text-slate-500 ml-auto">
          {loading ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
          {total.toLocaleString()} events
        </span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="audit-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((log, idx) => (
                <tr key={log.id || `log-${idx}`} className="hover:bg-slate-50 transition-colors" data-testid={`audit-row-${idx}`}>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap font-mono">
                    {formatTime(log.timestamp)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-800">
                    {log.user_email}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700 font-medium">
                    {ACTION_LABELS[log.action] || log.action}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${CATEGORY_COLORS[log.category] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {log.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500" style={{ maxWidth: '400px' }}>
                    <div className="truncate">{renderDetails(log)}</div>
                  </td>
                </tr>
              ))}
              {logs.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <ClipboardList size={40} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-400 text-sm">No audit events found</p>
                  </td>
                </tr>
              )}
              {loading && logs.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <Loader2 size={32} className="mx-auto animate-spin text-slate-300" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <span className="text-xs text-slate-500">
              Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, total)} of {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-sm border border-slate-200 disabled:opacity-30 hover:bg-slate-100"
                data-testid="audit-prev-page"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    className={`w-8 h-8 text-xs rounded-sm ${
                      page === p ? 'bg-[#0F172A] text-white' : 'border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-sm border border-slate-200 disabled:opacity-30 hover:bg-slate-100"
                data-testid="audit-next-page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
